import React from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import classNames from "../../../../utils/classNames";
import Notification from "../../../organisms/Notifications";
import EventCreateModal from "../../../molecules/EventCreateModal";
import ConfirmDeleteDialog from "../../../molecules/ConfirmDeleteDialog";
import ChangeProfilePictureModal from "../../../molecules/ProfilePictureUpload";
import BandPage from "../../BandPage";
import BandMemberInviteModal from "../../../organisms/Bands/BandMemberInviteModal";
import { AUDIO_MEDIA_ACCEPT } from "../../../../constants/media";
import { BAND_GALLERY_PHOTO_ACCEPT } from "../constants";

export default function SelectedBandDetailsView({
    className = "",
    onOpenMyBand,
    previewError,
    bandPageProps,
    coverUploadProps,
    avatarUploadProps,
    galleryUploadProps,
    trackUploadProps,
    eventModalProps,
    eventDeleteDialogProps,
    inviteMemberModalProps,
    memberRemoveDialogProps,
    leaveBandDialogProps,
    shareToastProps,
    mediaToastProps,
}) {
    const { t } = useTranslation();
    const canEdit = Boolean(bandPageProps.canEdit);
    const memberRemoveCopyKey = memberRemoveDialogProps.isPending
        ? "bandPage.revokeInvite"
        : "bandPage.removeMember";

    return (
        <main className={classNames("bands-welcome-page", "bands-welcome-page--band", className)}>
            {previewError ? (
                <div className="bands-welcome-page__band-error" role="alert">
                    {t("bandsWelcomePage.loadError")}
                </div>
            ) : null}
            <BandPage {...bandPageProps} onOpenMyBand={onOpenMyBand} />
            {canEdit && coverUploadProps.isOpen ? (
                <ChangeProfilePictureModal
                    mode="profileCover"
                    onClose={coverUploadProps.onClose}
                    onUploadSuccess={coverUploadProps.onUploadSuccess}
                    currentUrl={coverUploadProps.currentUrl}
                    mediaConfig={coverUploadProps.mediaConfig}
                />
            ) : null}
            {canEdit && avatarUploadProps.isOpen ? (
                <ChangeProfilePictureModal
                    mode="profilePicture"
                    onClose={avatarUploadProps.onClose}
                    onUploadSuccess={avatarUploadProps.onUploadSuccess}
                    currentUrl={avatarUploadProps.currentUrl}
                    mediaConfig={avatarUploadProps.mediaConfig}
                />
            ) : null}
            {canEdit ? (
                <input
                    ref={galleryUploadProps.inputRef}
                    type="file"
                    accept={BAND_GALLERY_PHOTO_ACCEPT}
                    className="bands-welcome-page__band-gallery-photo-input"
                    onChange={galleryUploadProps.onChange}
                    aria-hidden="true"
                    tabIndex={-1}
                    multiple
                />
            ) : null}
            {canEdit ? (
                <input
                    ref={trackUploadProps.inputRef}
                    type="file"
                    accept={AUDIO_MEDIA_ACCEPT}
                    className="bands-welcome-page__band-track-input"
                    onChange={trackUploadProps.onChange}
                    aria-hidden="true"
                    tabIndex={-1}
                />
            ) : null}
            {eventModalProps.isOpen ? (
                <EventCreateModal
                    isOpen={eventModalProps.isOpen}
                    mode={eventModalProps.mode}
                    event={eventModalProps.event}
                    onClose={eventModalProps.onClose}
                    onCreated={eventModalProps.onCreated}
                    onUpdated={eventModalProps.onUpdated}
                    onDeleted={eventModalProps.onDeleted}
                    organizer={eventModalProps.organizer}
                />
            ) : null}
            {canEdit && inviteMemberModalProps.isOpen ? (
                <BandMemberInviteModal {...inviteMemberModalProps} />
            ) : null}
            <ConfirmDeleteDialog
                isOpen={memberRemoveDialogProps.isOpen}
                title={t(`${memberRemoveCopyKey}.title`)}
                description={t(`${memberRemoveCopyKey}.description`)}
                targetName={memberRemoveDialogProps.targetName}
                confirmLabel={t(`${memberRemoveCopyKey}.confirm`)}
                cancelLabel={t("bandPage.removeMember.cancel")}
                isSubmitting={memberRemoveDialogProps.isSubmitting}
                errorMessage={memberRemoveDialogProps.error ? t(`${memberRemoveCopyKey}.error`) : null}
                onConfirm={memberRemoveDialogProps.onConfirm}
                onCancel={memberRemoveDialogProps.onCancel}
            />
            <ConfirmDeleteDialog
                isOpen={leaveBandDialogProps.isOpen}
                title={t("bandPage.leaveBand.title")}
                description={t("bandPage.leaveBand.description")}
                targetName={leaveBandDialogProps.targetName}
                confirmLabel={t("bandPage.leaveBand.confirm")}
                cancelLabel={t("bandPage.leaveBand.cancel")}
                isSubmitting={leaveBandDialogProps.isSubmitting}
                errorMessage={leaveBandDialogProps.error ? t("bandPage.leaveBand.error") : null}
                onConfirm={leaveBandDialogProps.onConfirm}
                onCancel={leaveBandDialogProps.onCancel}
            />
            <ConfirmDeleteDialog
                isOpen={eventDeleteDialogProps.isOpen}
                title={t("eventsPage.editModal.deleteDialog.title")}
                description={t("eventsPage.editModal.deleteDialog.description")}
                targetName={eventDeleteDialogProps.targetName}
                confirmLabel={t("eventsPage.editModal.deleteDialog.confirm")}
                cancelLabel={t("eventsPage.editModal.deleteDialog.cancel")}
                isSubmitting={eventDeleteDialogProps.isSubmitting}
                errorMessage={eventDeleteDialogProps.errorMessage}
                onConfirm={eventDeleteDialogProps.onConfirm}
                onCancel={eventDeleteDialogProps.onCancel}
            />
            {shareToastProps.toast ? (
                <Notification
                    key={shareToastProps.toast.id}
                    message={shareToastProps.toast.message}
                    type={shareToastProps.toast.type}
                    duration={3200}
                    onClose={shareToastProps.onClose}
                />
            ) : null}
            {mediaToastProps.toast ? (
                <Notification
                    key={mediaToastProps.toast.id}
                    message={mediaToastProps.toast.message}
                    type={mediaToastProps.toast.type}
                    duration={3200}
                    onClose={mediaToastProps.onClose}
                />
            ) : null}
        </main>
    );
}

const toastShape = PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    message: PropTypes.string.isRequired,
    type: PropTypes.string,
});

SelectedBandDetailsView.propTypes = {
    className: PropTypes.string,
    onOpenMyBand: PropTypes.func.isRequired,
    previewError: PropTypes.oneOfType([PropTypes.instanceOf(Error), PropTypes.object]),
    bandPageProps: PropTypes.shape({
        band: PropTypes.shape({
            id: PropTypes.string,
        }).isRequired,
        canEdit: PropTypes.bool,
    }).isRequired,
    coverUploadProps: PropTypes.shape({
        isOpen: PropTypes.bool.isRequired,
        onClose: PropTypes.func.isRequired,
        onUploadSuccess: PropTypes.func.isRequired,
        currentUrl: PropTypes.string,
        mediaConfig: PropTypes.shape({
            maxSizeMb: PropTypes.number,
            upload: PropTypes.func.isRequired,
            getHistory: PropTypes.func.isRequired,
        }).isRequired,
    }).isRequired,
    avatarUploadProps: PropTypes.shape({
        isOpen: PropTypes.bool.isRequired,
        onClose: PropTypes.func.isRequired,
        onUploadSuccess: PropTypes.func.isRequired,
        currentUrl: PropTypes.string,
        mediaConfig: PropTypes.shape({
            maxSizeMb: PropTypes.number,
            upload: PropTypes.func.isRequired,
            getHistory: PropTypes.func.isRequired,
        }).isRequired,
    }).isRequired,
    galleryUploadProps: PropTypes.shape({
        inputRef: PropTypes.shape({
            current: PropTypes.any,
        }).isRequired,
        onChange: PropTypes.func.isRequired,
    }).isRequired,
    trackUploadProps: PropTypes.shape({
        inputRef: PropTypes.shape({
            current: PropTypes.any,
        }).isRequired,
        onChange: PropTypes.func.isRequired,
    }).isRequired,
    eventModalProps: PropTypes.shape({
        isOpen: PropTypes.bool.isRequired,
        mode: PropTypes.string.isRequired,
        event: PropTypes.object,
        onClose: PropTypes.func.isRequired,
        onCreated: PropTypes.func.isRequired,
        onUpdated: PropTypes.func.isRequired,
        onDeleted: PropTypes.func.isRequired,
        organizer: PropTypes.shape({
            objectType: PropTypes.string.isRequired,
            objectId: PropTypes.string.isRequired,
        }),
    }).isRequired,
    eventDeleteDialogProps: PropTypes.shape({
        isOpen: PropTypes.bool.isRequired,
        targetName: PropTypes.string,
        isSubmitting: PropTypes.bool.isRequired,
        errorMessage: PropTypes.string,
        onConfirm: PropTypes.func.isRequired,
        onCancel: PropTypes.func.isRequired,
    }).isRequired,
    inviteMemberModalProps: PropTypes.shape({
        isOpen: PropTypes.bool.isRequired,
        bandName: PropTypes.string,
        searchTerm: PropTypes.string.isRequired,
        onSearchChange: PropTypes.func.isRequired,
        candidates: PropTypes.array.isRequired,
        isLoading: PropTypes.bool.isRequired,
        invitingUserId: PropTypes.string,
        error: PropTypes.oneOfType([PropTypes.instanceOf(Error), PropTypes.object, PropTypes.string]),
        onInvite: PropTypes.func.isRequired,
        onClose: PropTypes.func.isRequired,
    }).isRequired,
    memberRemoveDialogProps: PropTypes.shape({
        isOpen: PropTypes.bool.isRequired,
        targetName: PropTypes.string,
        isPending: PropTypes.bool,
        isSubmitting: PropTypes.bool.isRequired,
        error: PropTypes.oneOfType([PropTypes.instanceOf(Error), PropTypes.object, PropTypes.string]),
        onConfirm: PropTypes.func.isRequired,
        onCancel: PropTypes.func.isRequired,
    }).isRequired,
    leaveBandDialogProps: PropTypes.shape({
        isOpen: PropTypes.bool.isRequired,
        targetName: PropTypes.string,
        isSubmitting: PropTypes.bool.isRequired,
        error: PropTypes.oneOfType([PropTypes.instanceOf(Error), PropTypes.object, PropTypes.string]),
        onConfirm: PropTypes.func.isRequired,
        onCancel: PropTypes.func.isRequired,
    }).isRequired,
    shareToastProps: PropTypes.shape({
        toast: toastShape,
        onClose: PropTypes.func.isRequired,
    }).isRequired,
    mediaToastProps: PropTypes.shape({
        toast: toastShape,
        onClose: PropTypes.func.isRequired,
    }).isRequired,
};
